import _ from 'underscore';
import dayjs from 'dayjs';
import { useParams } from 'react-router';
import { useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import Highcharts, { SeriesOptionsType } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import ButtonGroup from 'components/button-group';
import LoadingSpinner from 'components/loading-spinner';

import { useIntl } from 'react-intl';
import { useGet } from 'hooks/use-get';
import 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/timeline/legend-symbols';
import { getTimeline } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/api/get-ofd-settings';
import { OFD2Params } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-2-params.type';
import { Timeline } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings.type';
import { cleanHighchartsData, cleanHighchartsValue } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/clean-data';
import {
  options,
  mapDates,
  reformatDates,
  renderSymbols,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/timeline';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';

const toUnix = (date: string) => dayjs(date.substring(0, 10)).local().valueOf();

const btnSx = {
  '&.MuiToggleButton-root.Mui-selected': {
    backgroundColor: '#BADBF2 !important',
  },
};

const INITIAL_DAYS = 45;
const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan';

/*
 * This method ensures that the chart xAxis max is always set to a relevant day based on available data (45 or 21 days).
 * It will check the timeline series in order (inventory, recommendations, consumption, orders)
 * and find the last date from the first series that has data.
 */
export const getMaxTimestamp = (timeline: Timeline): number | undefined => {
  const timelineSeries = [timeline.inventory, timeline.recommendations, timeline.consumption, timeline.orders];

  const activeSeries = timelineSeries.find((series) => series.length > 0);

  const lastPoint = activeSeries?.at(-1);

  return lastPoint ? new Date(lastPoint.date).getTime() : undefined;
};

const OFDTimeline = () => {
  const intl = useIntl();
  const theme = useTheme();
  const params = useParams<OFD2Params>();
  const { createTimelineEvent } = useGoogleTagManager();

  const [nrOfDays, setNrOfDays] = useState<number>(INITIAL_DAYS);

  const { data: timelineData, isLoading } = useGet(
    ['getTimeline', params.globalSiteId],
    ({ queryKey }) => getTimeline(String(queryKey[1]), INITIAL_DAYS),
    {
      enabled: !!params.globalSiteId,
    }
  );

  const timeline: Timeline = useMemo(() => {
    if (!timelineData)
      return {
        inventory: [],
        recommendations: [],
        consumption: [],
        orders: [],
        siloCapacity: NaN,
        buffer: NaN,
        skippedRecommendations: [],
        openSlots: [],
      } as Timeline;

    // Slice the data arrays to the selected number of days
    const slice = (arr: any[]) => arr.slice(0, nrOfDays);

    return {
      inventory: slice(cleanHighchartsData(timelineData.inventory)),
      recommendations: slice(cleanHighchartsData(timelineData.recommendations)),
      consumption: slice(cleanHighchartsData(timelineData.consumption)),
      orders: cleanHighchartsData(timelineData.orders),
      siloCapacity: cleanHighchartsValue(timelineData.siloCapacity),
      buffer: cleanHighchartsValue(timelineData.buffer),
      openSlots: slice(timelineData.openSlots || []),
      skippedRecommendations: slice(timelineData.skippedRecommendations || []),
    } as Timeline;
  }, [timelineData, nrOfDays]);

  const series: Highcharts.SeriesOptionsType[] = useMemo(() => {
    if (!timeline) return [];

    // Create forecasted inventory series by sorting and mapping inventory data and adding consumption value in inventory tooltip.
    const [sortedInventory, sortedConsumption] = [timeline.inventory, timeline.consumption].map((data) => reformatDates(data));

    const forecastedInventorySeries: SeriesOptionsType = {
      id: 'forecastedInventory',
      name: 'Forecasted Inventory',
      dashStyle: 'ShortDot',
      type: 'line',
      color: '#005B83',
      data: sortedInventory.map((inv) => ({
        x: toUnix(inv.date),
        y: inv.value,
        // Find the consumption value for the current inventory date, or use 'N/A' if not found.
        // total is the prop which will be used in the tooltip to display the consumption value.
        total: sortedConsumption.find((cons) => cons.date === inv.date)?.value || 'N/A',
      })),
      tooltip: {
        pointFormatter() {
          const style = `style="color: ${this.series.color}; font-weight: bold;"`;
          return `
            <span ${style}>•</span>
            <span>Forecasted Inventory: <b>${this.y}</b></span><br/>
            <span ${style}>•</span>
            <span>Forecasted Consumption: <b>${this.total}</b></span>
          `;
        },
      },
    };

    const confirmedOrderQtySeries: SeriesOptionsType = {
      id: 'confirmedOrderQty',
      name: 'Confirmed order qty.',
      type: 'column',
      color: '#97D7CA',
      borderColor: '#019776',
      pointWidth: 10,
      data: _.chain(timeline.orders)
        .groupBy((order) => toUnix(order.date))
        .map((orders, timestamp) => {
          const total = orders.reduce((sum, order) => sum + order.value, 0);
          const quantities = orders.map((order) => intl.formatNumber(order.value)).join(' + ');

          return {
            x: Number(timestamp),
            y: total,
            custom: {
              quantities: orders.length > 1 ? `${quantities} = ${intl.formatNumber(total)}` : quantities,
            },
          };
        })
        .sortBy((point) => point.x)
        .first(nrOfDays)
        .value(),
      tooltip: {
        pointFormat:
          '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.custom.quantities} kg</b><br/>',
      },
    };

    const forecastedOrderQtySeries: SeriesOptionsType = {
      id: 'forecatedOrderQty',
      name: 'Forecasted order qty.',
      type: 'column',
      color: '#EFDFD1',
      borderColor: '#809198',
      zIndex: 200,
      pointWidth: 10,
      data: _.chain(timeline.recommendations)
        .map((rec) => ({ x: toUnix(rec.date), y: rec.value }))
        .value(),
    };

    const skippedSeries: SeriesOptionsType = {
      id: 'skipped',
      name: 'Skipped',
      type: 'scatter',
      color: `${theme.palette.darkBlue.o25}`,
      marker: {
        symbol: 'skipped',
        lineWidth: 1,
        lineColor: 'black',
        radius: 5,
      },
      tooltip: { xDateFormat: '%A, %b %e %Y' },
      data: timeline.skippedRecommendations.map((date) => {
        return renderSymbols(date);
      }),
    };

    const openSeries: SeriesOptionsType = {
      id: 'open',
      name: 'Open',
      type: 'line',
      color: '#FFFFFF',
      marker: {
        symbol: 'circle',
        lineWidth: 1,
        lineColor: 'black',
        radius: 5,
      },
      data: timeline.openSlots.map((date) => {
        return renderSymbols(date);
      }),
    };

    const bufferSeries: SeriesOptionsType = {
      id: 'buffer',
      name: 'Buffer',
      color: '#FF9330',
      lineWidth: 1,
      type: 'line',
      tooltip: {
        headerFormat: '',
      },
      data: mapDates(
        timeline.inventory.map((inv) => inv.date),
        timeline.buffer
      ),
    };

    const capacitySeries: SeriesOptionsType = {
      id: 'capacity',
      name: 'Capacity',
      dashStyle: 'Solid',
      color: '#005B83',
      lineWidth: 1,
      type: 'line',
      tooltip: {
        headerFormat: '',
      },
      data: mapDates(
        timeline.inventory.map((inv) => inv.date),
        timeline.siloCapacity
      ),
    };

    return [
      forecastedInventorySeries,
      confirmedOrderQtySeries,
      forecastedOrderQtySeries,
      skippedSeries,
      openSeries,
      bufferSeries,
      capacitySeries,
    ];
  }, [timeline, intl, nrOfDays]);

  return (
    <>
      <Box display="flex" justifyContent="center" mt={4}>
        <ButtonGroup
          value={'group'}
          buttons={[
            {
              label: intl.formatMessage({ id: `${PAGE_NAME_ID}.timeline.fortyFiveDays` }),
              value: 'left',
              state: false,
              sx: btnSx,
              handleButtonClick: () => {
                setNrOfDays(45);
                createTimelineEvent('update_timeline_days', {
                  days: '45',
                });
              },
            },
            {
              label: intl.formatMessage({ id: `${PAGE_NAME_ID}.timeline.twentyOneDays` }),
              value: 'center',
              state: true,
              sx: btnSx,
              handleButtonClick: () => {
                setNrOfDays(21);
                createTimelineEvent('update_timeline_days', {
                  days: '21',
                });
              },
            },
          ]}
          handleClick={() => null}
        />
      </Box>
      {isLoading ? (
        <Box height={400} display={'flex'} alignItems={'center'}>
          <LoadingSpinner />
        </Box>
      ) : (
        <HighchartsReact
          highcharts={Highcharts}
          key={nrOfDays}
          options={{
            ...options,
            series: series,
            xAxis: {
              ...options.xAxis,
              max: getMaxTimestamp(timeline),
            },
          }}
        />
      )}
    </>
  );
};

export default OFDTimeline;
